import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { FormGroup, FormControl, HelpBlock, Button, ControlLabel, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Fa from 'react-fontawesome';
import { Field, reduxForm, SubmissionError, formValueSelector } from 'redux-form';
import Joi from 'joi';
import ProgressBar from '../../../../components/progress-bar';

import { requestUpdateOrganization } from '../../../Admin/AdminActions';
import { fetchAzureApps } from '../../SetupActions';

import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';

class MicrosoftDynamicsPage extends Component {
  constructor() {
    super();
    this.state = {
      loaded: false,
      error: null,
      apps: [],
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.onAppIdChange = this.onAppIdChange.bind(this);
    this.renderField = this.renderField.bind(this);
  }

  /** Load Power Apps from Azure API into state */
  loadApps() {
    this.props.dispatch(fetchAzureApps())
      .then(
        (apps) => {
          this.setState({ loaded: true, apps });
        },
        (error) => {
          this.setState({ error: error.data?.message || error.statusText });
        },
      );
  }

  componentDidMount() {
    this.loadApps();
  }

  /** Save new app id to organization */
  handleSubmit(data) {
    return this.props.dispatch(requestUpdateOrganization(this.props.organization._id, {
      dynamics_app_id: data.app_id_text,
    })).catch((error) => {
      // For errors, message seems standard, updating organization returns errors in organization_id property, fall back on the status text - MW 2023-03-01
      throw new SubmissionError({ app_id_text: true, _error: error.data?.message || error.data?.organization_id || error.statusText });
    });
  }

  /** Force unified interface app id inputs to reflect each other */
  onAppIdChange(_params, value, _previousValue, name) {
    if (name === 'app_id_text') {
      if (this.state.apps.find(app => app.value === value)) {
        this.props.change('app_id_select', value);
      } else {
        this.props.change('app_id_select', '');
      }
    } else if (name === 'app_id_select') {
      this.props.change('app_id_text', value);
    }
  }

  renderField(props) {
    const { submitting } = this.props;
    const { input, label, type, meta: { touched, error }, componentClass, placeholder, note, tip, options } = props;

    const tipIcon = (
      <OverlayTrigger key="button" trigger={['hover', 'focus']} placement="top" overlay={(
        <Tooltip id={`${input.name}_tip`}><span>{tip}</span></Tooltip>
      )}>
        <Fa name="info-circle" className="text-active" />
      </OverlayTrigger>
    );

    return (
      <FormGroup validationState={touched && error ? 'error' : null}>
        <ControlLabel>{label && `${label}: `}{tip && tipIcon}</ControlLabel>
        {(componentClass == null || componentClass === 'input') &&
          <FormControl {...input} type={type} placeholder={placeholder} disabled={false} />
        }
        {componentClass === 'select' &&
          <FormControl {...input} componentClass='select' disabled={!options?.length || submitting}>
            <option disabled>Please select a Unified Interface App:</option>
            {options != null && options.length && options.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
            <option value="">[Default Unified Interface App]</option>
          </FormControl>
        }
        <FormControl.Feedback />
        {note && <div><small><Fa name="info-circle" /> {note}</small></div>}
        {touched && error && <HelpBlock>{Array.isArray(error) ? error.join('. ') : error}</HelpBlock>}
      </FormGroup>
    );
  }

  renderForm() {
    const { handleSubmit, submitting, selectedDynamicsAppId, organization, error, valid } = this.props;
    const { apps } = this.state;

    const appIdTextLabel = 'Unified Interface App Id';
    const appIdTextNote = 'Leave empty to use the default app.';
    const appIdTextTip = 'Enter the Unified Interface App ID to use when generating Microsoft Dynamics URLs in TruSales.';
    const appIdTextPlaceholder = 'XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX';

    const appIdSelectLabel = 'Unified Interface App';
    const appIdSelectTip = `Select an App to autopopulate the Unified Interface App Id field.
      The TruSales Application User must have the Read Model-driven App permission and be granted access to the App for it to appear.`;

   return (
      <form onSubmit={handleSubmit(this.handleSubmit)}>
        <div className="panel panel-default">
          <div className="panel-heading">
            Microsoft Dynamics Settings:
          </div>
          <div className="panel-body">
            {error !== null && <div className="alert alert-danger">{error}</div>}
            <FormGroup>
              <div className="row">
                <div className="col-xs-6">
                  <Field
                    name="app_id_select"
                    componentClass="select"
                    label={appIdSelectLabel}
                    tip={appIdSelectTip}
                    options={apps}
                    component={this.renderField}
                    onChange={this.onAppIdChange}
                  />
                  {selectedDynamicsAppId !== null && selectedDynamicsAppId !== undefined && selectedDynamicsAppId !== '' &&
                    <div>
                      <em>Unified Interface App Id: </em> <code>{selectedDynamicsAppId}</code>
                    </div>
                  }
                </div>
              </div>
              {false && <div className="row">
                <div className="col-xs-6">
                  <Field
                    name="app_id_text"
                    type="text"
                    label={appIdTextLabel}
                    placeholder={appIdTextPlaceholder}
                    note={appIdTextNote}
                    tip={appIdTextTip}
                    component={this.renderField}
                    onChange={this.onAppIdChange}
                  />
                </div>
              </div>}
            </FormGroup>
          </div>
          <div className="panel-footer text-right">
            <Button disabled={(selectedDynamicsAppId === organization.dynamics_app_id) || !valid || submitting} bsStyle="primary" type="submit">
              {submitting && <Fa name="spinner" spin />} Update Settings
            </Button>
          </div>
        </div>
      </form>
    );
  }

  render() {
    const { loaded, error } = this.state;
    return (
      <div>
        <Helmet title="Microsoft Dynamics Settings | Organization Setup" />
        <div className="container">
          {!loaded && !error && <ProgressBar />}
          {loaded && !error && this.renderForm()}
          {error && <div className="alert alert-warning">{error}</div>}
        </div>
      </div>
    );
  }
}

const selector = formValueSelector('microsoftDynamicsForm');

function mapStateToProps(state) {
  const organization = getUserCurrentOrganization(state);
  return {
    initialValues: {
      app_id_text: organization.dynamics_app_id || '',
      app_id_select: organization.dynamics_app_id || '',
    },
    selectedDynamicsAppId: selector(state, 'app_id_text'),
    organization,
  };
}

MicrosoftDynamicsPage.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  selectedDynamicsAppId: PropTypes.string,
  organization: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    dynamics_app_id: PropTypes.string,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  change: PropTypes.func,
  error: PropTypes.string,
  valid: PropTypes.bool,
  submitting: PropTypes.bool,
};
MicrosoftDynamicsPage.defaultProps = {
  error: null,
  valid: false,
  submitting: false,
};

const validate = (values) => {
  // Legacy constraints for reference:
  // const constraints = {
  //   app_id_text: {
  //     format: {
  //       pattern: '^$|^[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$',
  //       flags: "i",
  //       message: 'is not a valid GUID or empty',
  //     },
  //   },
  // };

  const schema = Joi.object({
    app_id_text: Joi.string().allow('', null)
      .pattern(/^$|^[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/i)
      .messages({
        'string.base': 'App ID must be a string',
        'string.pattern.base': 'App ID is not a valid GUID or empty',
      }),
  }).unknown(true);

  const { error } = schema.validate(values, { abortEarly: false });
  if (!error) return {};
  
  return error.details.reduce((acc, curr) => {
    if (!acc[curr.path[0]]) acc[curr.path[0]] = [];
    acc[curr.path[0]].push(curr.message);
    return acc;
  }, {});
};

export default connect(mapStateToProps)(reduxForm({
  form: 'microsoftDynamicsForm',
  validate,
})(MicrosoftDynamicsPage));
